import React, { useEffect, useMemo, useState } from 'react';
import { useCaps } from '../../../contexts/CapabilitiesContext';
import { pillActive, pillInactive } from '../styles';
import { useOrderedSubtabs } from '../subtabs';
import PlatformManagementPanel from './PlatformManagementPanel';
import TabsOrderingPanel from './TabsOrderingPanel';
import AdminCurrencyConversionTab from '../currency/AdminCurrencyConversionTab';
import { MISC_SUBTAB_CAPS } from './types';

type SubtabKey = 'platform' | 'tabs-ordering' | 'currency-conversion';

const SUBTABS: { key: SubtabKey; label: string; cap: string }[] = [
  {
    key: 'platform',
    label: 'Platform management',
    cap: MISC_SUBTAB_CAPS.platform,
  },
  {
    key: 'tabs-ordering',
    label: 'Admin Panel Tabs Ordering',
    cap: MISC_SUBTAB_CAPS.tabsOrdering,
  },
  {
    key: 'currency-conversion',
    label: 'Currency Conversion',
    cap: MISC_SUBTAB_CAPS.currencyConversion,
  },
];

const AdminMiscTab: React.FC = () => {
  const { can } = useCaps();
  // Keep a stable reference for the hook; capability filter still applied inside.
  const declared = useMemo(() => SUBTABS, []);
  const visibleSubtabs = useOrderedSubtabs('misc', declared);
  const [active, setActive] = useState<SubtabKey | null>(null);

  useEffect(() => {
    setActive((current) => {
      if (current && visibleSubtabs.some((t) => t.key === current)) return current;
      return (visibleSubtabs[0]?.key as SubtabKey | undefined) ?? null;
    });
  }, [visibleSubtabs]);

  // Fallback when DB has no misc subtabs yet: still respect caps
  const fallbackVisible = useMemo(
    () => SUBTABS.filter((tab) => can(tab.cap)),
    [can]
  );
  const pills = visibleSubtabs.length > 0 ? visibleSubtabs : fallbackVisible;

  if (pills.length === 0) {
    return (
      <div className="text-sm text-[#8A8A8A]">
        You do not have access to any misc tools in this panel.
      </div>
    );
  }

  const activeKey = active ?? (pills[0]?.key as SubtabKey);

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-5">
        {pills.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActive(tab.key as SubtabKey)}
            className={`h-8 px-3 rounded-full text-[13px] font-medium transition-colors ${
              activeKey === tab.key ? pillActive : pillInactive
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {activeKey === 'platform' && <PlatformManagementPanel />}
      {activeKey === 'tabs-ordering' && <TabsOrderingPanel />}
      {activeKey === 'currency-conversion' && <AdminCurrencyConversionTab />}
    </div>
  );
};

export default AdminMiscTab;
